import { useNavigate } from "react-router-dom";
import PaymentBadges from "@/components/feature/PaymentBadges";
import { useThemeColor, hexAlpha } from "@/hooks/useThemeColor";
import { useStoreSettings, DAY_ORDER, DAY_LABELS } from "@/hooks/useStoreSettings";
import { useDesignState } from "@/hooks/useDesignSystem";
import { FOOTER_STYLES } from "@/pages/admin/components/design/designConstants";
import EbdaaLogo from "@/components/feature/EbdaaLogo";

const scrollTo = (id: string) => {
  document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
};

function formatTime12(t: string): string {
  const [h, m] = t.split(":").map(Number);
  const period = h < 12 ? "ص" : "م";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function getTodayKey(): typeof DAY_ORDER[number] {
  const jsDay = new Date().getDay();
  const map: Record<number, typeof DAY_ORDER[number]> = {
    0: "sunday", 1: "monday", 2: "tuesday",
    3: "wednesday", 4: "thursday", 5: "friday", 6: "saturday",
  };
  return map[jsDay];
}

export default function Footer() {
  const navigate = useNavigate();
  const tc = useThemeColor();
  const { settings } = useStoreSettings();
  const { brand, identity } = useDesignState();
  const bh = settings.businessHours;
  const todayKey = getTodayKey();
  const todayEntry = bh?.[todayKey];

  // Footer style from design settings
  const footerStyle = FOOTER_STYLES.find(f => f.id === brand.footerStyle) ?? FOOTER_STYLES[0];
  const footerBg = footerStyle.bg;
  const footerTextColor = footerStyle.text;

  // Copyright & tagline from brand settings
  const copyright = brand.footerCopyright || '© 2026 متجر الإبداع — AL Ebdaa Game Store. جميع الحقوق محفوظة.';
  const tagline = brand.footerTagline || 'متجرك المتخصص في أجهزة الألعاب والإكسسوارات الاحترافية.';

  // Social links from identity
  const socials = [
    { icon: 'ri-instagram-fill', url: identity.socialInstagram, label: 'Instagram' },
    { icon: 'ri-twitter-x-fill', url: identity.socialTwitter, label: 'Twitter' },
    { icon: 'ri-snapchat-fill', url: identity.socialSnapchat, label: 'Snapchat' },
    { icon: 'ri-tiktok-fill', url: identity.socialTiktok, label: 'TikTok' },
  ].filter(s => s.url);

  // If no social links configured, show defaults
  const socialIcons = socials.length > 0
    ? socials
    : [
        { icon: 'ri-instagram-fill', url: '#', label: 'Instagram' },
        { icon: 'ri-twitter-x-fill', url: '#', label: 'Twitter' },
        { icon: 'ri-snapchat-fill', url: '#', label: 'Snapchat' },
        { icon: 'ri-tiktok-fill', url: '#', label: 'TikTok' },
      ];

  const isOpen = (() => {
    if (!todayEntry || todayEntry.closed) return false;
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const [oh, om] = todayEntry.open.split(":").map(Number);
    const [ch, cm] = todayEntry.close.split(":").map(Number);
    return nowMins >= oh * 60 + om && nowMins < ch * 60 + cm;
  })();

  return (
    <footer
      className="border-t pt-14 pb-8 px-4 transition-colors duration-500"
      style={{ background: footerBg, borderTopColor: hexAlpha(tc.primary, 0.1) }}
      dir="rtl"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            <div className="mb-4">
              <EbdaaLogo size="md" showText subText="GAME STORE" />
            </div>
            <p className="text-sm leading-relaxed" style={{ color: footerTextColor, fontFamily: "'Cairo', sans-serif", opacity: 0.7 }}>
              {tagline}
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-bold text-sm mb-5" style={{ color: '#ffffff', fontFamily: "'Cairo', sans-serif" }}>روابط سريعة</h4>
            <ul className="flex flex-col gap-3">
              {[
                { label: "منتجاتنا", href: "#products" },
                { label: "الأجهزة", href: "#devices" },
                { label: "الإكسسوارات", href: "#accessories" },
                { label: "آراء العملاء", href: "#reviews" },
                { label: "تواصل معنا", href: "#contact" },
              ].map((l) => (
                <li key={l.label}>
                  <button onClick={() => scrollTo(l.href)}
                    className="text-sm transition-colors cursor-pointer whitespace-nowrap"
                    style={{ color: footerTextColor, fontFamily: "'Cairo', sans-serif", opacity: 0.7 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = tc.primary; (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = footerTextColor; (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}>
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links extra */}
          <div>
            <h4 className="font-bold text-sm mb-5" style={{ color: '#ffffff', fontFamily: "'Cairo', sans-serif" }}>خدمات العملاء</h4>
            <ul className="flex flex-col gap-3">
              {[
                { label: "تتبع طلبك", path: "/track-order", icon: "ri-map-pin-time-line" },
                { label: "حسابي", path: "/account", icon: "ri-user-line" },
                { label: "نقاط المكافآت", path: "/rewards", icon: "ri-vip-crown-line" },
                { label: "العروض والتخفيضات", path: "/deals", icon: "ri-flashlight-line" },
                { label: "المفضلة", path: "/favorites", icon: "ri-heart-line" },
                { label: "من نحن", path: "/about", icon: "ri-information-line" },
                { label: "سياسة الخصوصية", path: "/privacy-policy", icon: "ri-shield-check-line" },
                { label: "الشروط والأحكام", path: "/terms", icon: "ri-file-text-line" },
              ].map((l) => (
                <li key={l.label}>
                  <button onClick={() => navigate(l.path)}
                    className="flex items-center gap-2 text-sm transition-colors cursor-pointer whitespace-nowrap"
                    style={{ color: footerTextColor, fontFamily: "'Cairo', sans-serif", opacity: 0.7 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = tc.primary; (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = footerTextColor; (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}>
                    <i className={`${l.icon} text-xs`}></i>
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Business Hours */}
          <div>
            <h4 className="font-bold text-sm mb-5" style={{ color: '#ffffff', fontFamily: "'Cairo', sans-serif" }}>تواصل معنا</h4>
            <ul className="flex flex-col gap-4 mb-6">
              {[
                { icon: "ri-phone-fill", text: "+966 54 113 8847" },
                { icon: "ri-whatsapp-fill", text: "واتساب متاح 24/7" },
                { icon: "ri-map-pin-fill", text: "الطائف، المملكة العربية السعودية" },
              ].map((c) => (
                <li key={c.text} className="flex items-center gap-3">
                  <i className={`${c.icon} text-sm`} style={{ color: tc.primary }}></i>
                  <span className="text-sm" style={{ color: footerTextColor, fontFamily: "'Cairo', sans-serif", opacity: 0.7 }}>{c.text}</span>
                </li>
              ))}
            </ul>

            {/* Business Hours */}
            {bh && brand.footerShowHours !== false && (
              <div className="rounded-xl overflow-hidden border" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.08)' }}>
                <div className="flex items-center justify-between px-3 py-2.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line text-xs" style={{ color: tc.primary }}></i>
                    <span className="text-xs font-bold" style={{ color: '#ffffff', fontFamily: "'Cairo', sans-serif" }}>ساعات العمل</span>
                  </div>
                  <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    isOpen ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"
                  }`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <span className={`w-1 h-1 rounded-full ${isOpen ? "bg-green-400 animate-pulse" : "bg-red-400"}`}></span>
                    {isOpen ? "مفتوح" : "مغلق"}
                  </span>
                </div>
                <div className="px-3 py-2 flex flex-col gap-1.5">
                  {DAY_ORDER.map((day) => {
                    const entry = bh[day];
                    const isToday = day === todayKey;
                    return (
                      <div key={day} className={`flex items-center justify-between ${isToday ? "opacity-100" : "opacity-60"}`}>
                        <span className={`text-[11px] ${isToday ? "font-bold" : "font-normal"}`}
                          style={{ fontFamily: "'Cairo', sans-serif", color: isToday ? tc.primary : footerTextColor }}>
                          {DAY_LABELS[day]}
                        </span>
                        {entry.closed ? (
                          <span className="text-[10px] text-red-400/70" style={{ fontFamily: "'Cairo', sans-serif" }}>مغلق</span>
                        ) : (
                          <span className="text-[10px]" style={{ fontFamily: "'Cairo', sans-serif", color: isToday ? tc.primary : footerTextColor }} dir="rtl">
                            {formatTime12(entry.open)}–{formatTime12(entry.close)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Social Icons */}
            {brand.footerShowSocial !== false && (
              <div className="flex gap-3 mt-5">
                {socialIcons.map((s) => (
                  <a key={s.icon} href={s.url} target="_blank" rel="noopener noreferrer"
                    className="w-8 h-8 flex items-center justify-center border rounded-lg transition-all cursor-pointer text-sm"
                    style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: footerTextColor }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.4);
                      (e.currentTarget as HTMLElement).style.color = tc.primary;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.1)';
                      (e.currentTarget as HTMLElement).style.color = footerTextColor;
                    }}>
                    <i className={s.icon}></i>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Payment Methods */}
        {brand.footerShowPayments !== false && (
          <div className="border-t border-b py-6 mb-6" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shield-check-line text-base" style={{ color: tc.primary }}></i>
                </div>
                <span className="text-sm font-semibold" style={{ color: footerTextColor, fontFamily: "'Cairo', sans-serif", opacity: 0.8 }}>
                  طرق الدفع المقبولة
                </span>
              </div>
              <PaymentBadges size="sm" />
            </div>
          </div>
        )}

        {/* Bottom bar */}
        <div className="border-t pt-6 flex flex-col sm:flex-row items-center justify-between gap-3" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
          <p className="text-xs" style={{ color: footerTextColor, fontFamily: "'Cairo', sans-serif", opacity: 0.5 }}>
            {copyright}
          </p>
          <p className="text-xs" style={{ color: footerTextColor, fontFamily: "'Cairo', sans-serif", opacity: 0.4 }}>
            تم إنشاء الموقع بواسطة <span style={{ opacity: 0.7, fontWeight: 700 }}>Toran101</span>
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            {[
              { label: "من نحن", path: "/about" },
              { label: "سياسة الخصوصية", path: "/privacy-policy" },
              { label: "الشروط والأحكام", path: "/terms" },
            ].map((l, i) => (
              <>
                {i > 0 && <span key={`sep-${i}`} className="text-xs" style={{ color: footerTextColor, opacity: 0.2 }}>|</span>}
                <button key={l.label} onClick={() => navigate(l.path)}
                  className="text-xs transition-colors cursor-pointer whitespace-nowrap"
                  style={{ color: footerTextColor, fontFamily: "'Cairo', sans-serif", opacity: 0.5 }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.opacity = '0.9'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.opacity = '0.5'}>
                  {l.label}
                </button>
              </>
            ))}
            <span className="text-xs" style={{ color: footerTextColor, opacity: 0.2 }}>|</span>
            {["PS5", "PS4", "PC"].map((b) => (
              <span key={b} className="text-xs font-bold tracking-wider" style={{ color: footerTextColor, opacity: 0.4 }}>{b}</span>
            ))}
            <button onClick={() => navigate("/admin")} title="إدارة المتجر"
              className="w-5 h-5 flex items-center justify-center opacity-10 hover:opacity-60 transition-opacity duration-300 cursor-pointer"
              aria-label="إدارة المتجر">
              <i className="ri-lock-line text-xs" style={{ color: footerTextColor }}></i>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
